import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { supabaseFetch } from '../../../lib/supabase';
import { getMyStewardships } from '../../../data/directoryService';
import { isPlatformAdmin } from '../../../utils/adminHelpers';
import { isSignedIn } from '../../../contexts/AuthContext';
import useInstallPrompt from '../../../hooks/useInstallPrompt';

function ProfileMenu({ isOpen, onClose, reputation = 6.3, currentUser }) {
    const menuRef = useRef(null);
    const { t } = useTranslation('ui');
    const { t: tS } = useTranslation('steward');
    // PWA install availability — shared hook (single source of truth). Desktop surface
    // gates purely on canInstall: shows the button only when the native prompt is real,
    // and renders nothing otherwise. No iOS Add-to-Home-Screen instructions here — those
    // belong on the mobile surface (InstallAppPrompt), not a desktop profile menu.
    const { canInstall, install } = useInstallPrompt();
    const [showReputationModal, setShowReputationModal] = useState(false);
    const [reputationData, setReputationData] = useState(null);
    const [smartLinkArticle, setSmartLinkArticle] = useState(null);
    const [showStewardEntry, setShowStewardEntry] = useState(false);
    const modalRef = useRef(null);

    // Determine Steward menu entry visibility when menu opens
    useEffect(() => {
        if (!isOpen || !isSignedIn(currentUser)) {
            setShowStewardEntry(false);
            return;
        }
        if (isPlatformAdmin(currentUser)) {
            setShowStewardEntry(true);
            return;
        }
        getMyStewardships().then(rows => {
            setShowStewardEntry(rows.length > 0);
        });
    }, [isOpen, currentUser]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    // Load reputation data when menu opens
    useEffect(() => {
        const loadReputation = async () => {
            if (!isOpen || !currentUser?.id) return;

            try {
                // schema 'public': the viewer's own reputation, even while viewing a demo (Phase B1)
                const { data, error } = await supabaseFetch.query('user_reputation', {
                    select: '*',
                    eq: { user_id: currentUser.id },
                    single: true,
                    schema: 'public'
                });

                if (error && error.code !== 'PGRST116') {
                    console.error('Error loading reputation:', error);
                    return;
                }

                setReputationData(data);
            } catch (err) {
                console.error('Error loading reputation:', err);
            }
        };

        loadReputation();
    }, [isOpen, currentUser]);

    // Load SmartLink article
    useEffect(() => {
        const loadSmartLink = async () => {
            try {
                // schema 'public' (both reads): a live platform article, even while viewing a demo
                const { data: smartLink, error: linkError } = await supabaseFetch.query('smart_links', {
                    select: 'target_id',
                    eq: { slug: 'reputation-explained' },
                    single: true,
                    schema: 'public'
                });

                if (linkError || !smartLink) return;

                const { data: article, error: articleError } = await supabaseFetch.query('articles', {
                    select: 'id,title,is_published',
                    eq: { id: smartLink.target_id },
                    single: true,
                    schema: 'public'
                });

                if (articleError || !article || !article.is_published) return;

                setSmartLinkArticle(article);
            } catch (err) {
                console.error('Error loading SmartLink:', err);
            }
        };

        loadSmartLink();
    }, []);

    // Close modal when clicking outside
    useEffect(() => {
        const handleModalClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setShowReputationModal(false);
            }
        };

        if (showReputationModal) {
            document.addEventListener('mousedown', handleModalClickOutside);
            return () => document.removeEventListener('mousedown', handleModalClickOutside);
        }
    }, [showReputationModal]);

    // Generate contextual feedback statements
    const generateFeedback = () => {
        if (!reputationData || !reputationData.feedback_data) {
            return [t('rep_neutral')];
        }

        const fd = reputationData.feedback_data;
        const statements = [];

        if (fd.top_content && fd.top_content.length > 0) {
            const top = fd.top_content[0];
            if (top.share_count > 0) {
                const key = top.share_count === 1 ? 'rep_article_shared_one' : 'rep_article_shared_many';
                statements.push(t(key, { title: top.title, count: top.share_count }));
            } else if (top.like_count > 0) {
                const key = top.like_count === 1 ? 'rep_article_liked_one' : 'rep_article_liked_many';
                statements.push(t(key, { title: top.title, count: top.like_count }));
            }
        }

        if (fd.engagement_count < 5) {
            if (statements.length === 0) {
                statements.push(t('rep_neutral'));
            }
        } else if (fd.below_negative_threshold) {
            statements.push(t('rep_negative_ratings'));
        } else if (fd.above_positive_threshold) {
            statements.push(t('rep_balanced_constructive'));
        } else {
            statements.push(t('rep_balanced_feedback'));
        }

        if (fd.content_count > 0 && fd.engagement_count >= 5) {
            if (fd.dominant_factor === 'content_quality') {
                statements.push(t('rep_driven_quality'));
            } else {
                statements.push(t('rep_driven_engagement'));
            }
        }

        return statements.length > 0 ? statements : [t('rep_neutral_short')];
    };

    const handleArticleClick = () => {
        if (smartLinkArticle) {
            window.dispatchEvent(new CustomEvent('ournet:navigate', {
                detail: { view: 'article', articleId: smartLinkArticle.id }
            }));
            setShowReputationModal(false);
            onClose();
        }
    };

    // Get needle icon based on meter score
    const getNeedleIcon = (score) => {
        const rounded = Math.round(score);
        const clamped = Math.max(-4, Math.min(4, rounded));

        if (clamped === 0) return '/images/icons/Reps/Rep0.png';
        if (clamped > 0) return `/images/icons/Reps/Rep+${clamped}.png`;
        return `/images/icons/Reps/Rep${clamped}.png`;
    };

    const handleNavigation = (path, userId = null) => {
        // Use a custom event so the top-level App can switch views
        const explicitUserId = userId || currentUser?.id || null;
        console.log('Request navigation to:', path, 'userId:', explicitUserId);
        try {
            window.dispatchEvent(new CustomEvent('ournet:navigate', { detail: { view: path, userId: explicitUserId } }));
        } catch (err) {
            // Fallback to location if CustomEvent isn't supported
            console.error('Custom navigation event failed, falling back to window.location', err);
            window.location.href = path;
        }
        // Close the menu after navigation
        if (onClose) onClose();
    };

    if (!isOpen) return null;

    return (
        <div
            ref={menuRef}
            style={{
                position: 'absolute',
                top: '4px',
                right: '0px',
                width: '292px',
                height: '292px',
                zIndex: 9998,
                display: isOpen ? 'block' : 'none',
                pointerEvents: 'auto'
            }}
        >
            {/* Circular quadrant background */}
            <svg
                width={293}
                height={293}
                viewBox="0 0 293 293"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3))'
                }}
            >
                <path
                    d="M291.999 291.995C131.128 291.726 0.771585 161.369 0.501966 0.500013L291.999 0.5L291.999 291.995Z"
                    fill="#F9A61B"
                    stroke="#F9A61B" />
            </svg>

            {/* Profile Button - 15 degrees from horizontal */}
            <div
                onClick={() => handleNavigation('profile')}
                style={{
                    position: 'absolute',
                    top: '13px',
                    right: '13px',
                    cursor: 'pointer',
                    zIndex: 9999,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                <img
                    src="/images/icons/icon-profile-white.png"
                    alt=""
                    style={{ width: '50px', height: '50px' }}
                    onError={(e) => {
                        e.target.style.display = 'none';
                    } } />
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#1A1A1A', fontFamily: 'OpenSauceSans', marginTop: '2px' }}>{t('menu_profile')}</span>
            </div>

            {/* Locations Button - 35 degrees from horizontal */}
            {(
            <div
                onClick={() => handleNavigation('locations')}
                style={{
                    position: 'absolute',
                    top: '13px',
                    left: '35px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                <img
                    src="/images/icons/icon-locations-white.png"
                    alt=""
                    style={{ width: '50px', height: '50px' }}
                    onError={(e) => {
                        e.target.style.display = 'none';
                    } } />
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#1A1A1A', fontFamily: 'OpenSauceSans', marginTop: '2px' }}>{t('menu_locations')}</span>
            </div>
            )}

            {/* Install Desktop App Button - under Profile, centred on Profile's column (right 13px +
                50px icon → centre 38px from the right edge). Moved here from between Locations and
                Profile (docs/briefs/2026-09-18-header-icons-and-profile-dial-layout-fixes.md §2) so
                the Steward entry can take that slot; 56px column fits the pt-PT "Aplicação". */}
            {canInstall && (
                <div
                    onClick={install}
                    style={{
                        position: 'absolute',
                        top: '90px',
                        right: '10px',
                        width: '56px',
                        cursor: 'pointer',
                        zIndex: 9999
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#1A1A1A',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            lineHeight: '1.1',
                            textAlign: 'center'
                        }}
                    >
                        <div>{t('install')}</div>
                        <div>{t('desktop')}</div>
                        <div>{t('app')}</div>
                    </div>
                </div>
            )}

            {/* Steward Workspace - text-only, like Install button. In the slot between Locations
                and Profile (formerly Install's); at top 115 / left 100 it sat under the Groups
                button (layout brief 2026-09-18 §2). */}
            {showStewardEntry && (
                <div
                    onClick={() => handleNavigation('steward')}
                    style={{
                        position: 'absolute',
                        top: '20px',
                        left: '130px',
                        cursor: 'pointer',
                        zIndex: 9999
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#1A1A1A',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            lineHeight: '1.1',
                            textAlign: 'center',
                            fontFamily: 'OpenSauceSans'
                        }}
                    >
                        <div>{tS('nav_label')}</div>
                    </div>
                </div>
            )}

            {/* Articles Button - 45 degrees from horizontal */}
            <div
                onClick={() => handleNavigation('articles')}
                style={{
                    position: 'absolute',
                    top: '144px',
                    right: '143px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                <img
                    src="/images/icons/icon-articles-white.png"
                    alt=""
                    style={{ width: '50px', height: '50px' }}
                    onError={(e) => {
                        e.target.style.display = 'none';
                    } } />
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#1A1A1A', fontFamily: 'OpenSauceSans', marginTop: '2px' }}>{t('menu_articles')}</span>
            </div>

            {/* Tags Button - 75 degrees from horizontal */}
            <div
                onClick={() => handleNavigation('tags')}
                style={{
                    position: 'absolute',
                    top: '210px',
                    right: '13px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                <img
                    src="/images/icons/icon-tags-white.png"
                    alt=""
                    style={{ width: '50px', height: '50px' }}
                    onError={(e) => {
                        e.target.style.display = 'none';
                    } } />
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#1A1A1A', fontFamily: 'OpenSauceSans', marginTop: '2px' }}>{t('menu_tags')}</span>
            </div>

            {/* My Images Button - Between Articles and Tags */}
            <div
                onClick={() => handleNavigation('images')}
                style={{
                    position: 'absolute',
                    top: '178px',
                    right: '78px',
                    cursor: 'pointer',
                    zIndex: 9999,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                <img
                    src="/images/icons/icon-images-white.png"
                    alt=""
                    style={{ width: '60px', height: '60px', filter: 'brightness(1) invert(0)' }}
                    onError={(e) => {
                        e.target.style.display = 'none';
                    } } />
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#1A1A1A', fontFamily: 'OpenSauceSans', marginTop: '-4px' }}>{t('menu_images')}</span>
            </div>

            {/* My Groups Button - Between Locations and Articles */}
            <div
                onClick={() => handleNavigation('my-groups')}
                style={{
                    position: 'absolute',
                    top: '77px',
                    left: '52px',
                    cursor: 'pointer',
                    zIndex: 9999,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                <img
                    src="/images/icons/icon-groups-white.png"
                    alt=""
                    style={{ width: '60px', height: '60px' }}
                    onError={(e) => {
                        e.target.style.display = 'none';
                    } } />
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#1A1A1A', fontFamily: 'OpenSauceSans', marginTop: '-4px' }}>{t('menu_groups')}</span>
            </div>

            {/* Reputation Meter - Top Right - Interactive */}
            <div
                onMouseEnter={() => setShowReputationModal(true)}
                onMouseLeave={() => setShowReputationModal(false)}
                style={{
                    position: 'absolute',
                    top: '75px',
                    right: '65px',
                    cursor: 'pointer',
                    width: '85px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                <div style={{ position: 'relative', width: '85px', height: '85px' }}>
                    <img
                        src={reputationData ? getNeedleIcon(reputationData.meter_score ?? 0.0) : '/images/icons/Reps/Rep0.png'}
                        alt=""
                        style={{
                            width: '85px',
                            height: '85px',
                            objectFit: 'contain'
                        }}
                        onError={(e) => {
                            e.target.src = '/images/icons/icon-reputation.png';
                        }}
                    />
                    {/* Score overlay — sits inside the white pivot circle */}
                    <div style={{
                        position: 'absolute',
                        bottom: '35px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        fontFamily: 'OpenSauceSans',
                        color: '#1e293b',
                        textShadow: '0 1px 2px rgba(255,255,255,0.8)',
                        whiteSpace: 'nowrap'
                    }}>
                        {reputationData ? (reputationData.meter_score ?? 0.0).toFixed(1) : '0.0'}
                    </div>
                </div>
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#1A1A1A', fontFamily: 'OpenSauceSans', marginTop: '-32px', textAlign: 'center', width: '85px', position: 'relative', zIndex: 1 }}>{t('menu_reputation')}</span>
            </div>


            {/* Reputation Tooltip - appears on hover */}
            {showReputationModal && (
                <div
                    ref={modalRef}
                    onMouseEnter={() => setShowReputationModal(true)}
                    onMouseLeave={() => setShowReputationModal(false)}
                    style={{
                        position: 'absolute',
                        top: '150px',
                        right: '10px',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '24px',
                        width: '400px',
                        maxHeight: '500px',
                        overflowY: 'auto',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
                        zIndex: 10001,
                        border: '2px solid #F9A61B'
                    }}
                >
                        {/* Header */}
                        <div style={{
                            marginBottom: '16px'
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '18px',
                                fontWeight: 'bold',
                                fontFamily: 'OpenSauceSans',
                                color: '#1e293b'
                            }}>
                                {t('rep_your_reputation', { score: reputationData ? (reputationData.meter_score ?? 0.0).toFixed(1) : '0.0' })}
                            </h3>
                        </div>

                        {/* Explanation paragraph */}
                        <div style={{
                            padding: '12px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '6px',
                            marginBottom: '16px',
                            fontSize: '13px',
                            lineHeight: '1.5',
                            color: '#1e293b',
                            fontFamily: 'OpenSauceSans'
                        }}>
                            {t('rep_what_is')}
                        </div>

                        {/* Contextual feedback statements */}
                        <div style={{ marginBottom: '16px' }}>
                            <h4 style={{
                                margin: '0 0 8px 0',
                                fontSize: '14px',
                                fontWeight: '600',
                                fontFamily: 'OpenSauceSans',
                                color: '#1e293b'
                            }}>
                                {t('rep_affecting_score')}
                            </h4>
                            <ul style={{
                                margin: 0,
                                padding: '0 0 0 20px',
                                fontSize: '13px',
                                lineHeight: '1.6',
                                color: '#1e293b',
                                fontFamily: 'OpenSauceSans'
                            }}>
                                {generateFeedback().map((statement, index) => (
                                    <li key={index} style={{ marginBottom: '6px' }}>
                                        {statement}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* SmartLink to explanation article */}
                        {smartLinkArticle && (
                            <div style={{
                                marginTop: '12px',
                                paddingTop: '12px',
                                borderTop: '1px solid #e5e7eb'
                            }}>
                                <button
                                    onClick={handleArticleClick}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#024B99',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        textDecoration: 'none',
                                        fontFamily: 'OpenSauceSans',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: 0
                                    }}
                                >
                                    <span>{smartLinkArticle.title}</span>
                                    <span style={{ fontSize: '16px' }}>→</span>
                                </button>
                            </div>
                        )}
                </div>
            )}

        </div>
    );
}

export default ProfileMenu;
